import type { ConnectionDef, StepConfig, WorkflowConfig } from '@reef/schema';
import { MAX_STEP_LEVEL } from '@reef/schema';
import {
  DUAL_IDENTITY,
  IdentityBadge,
  botLabelFor,
  identityKindFor,
  identityTip,
} from './IdentityBadge.tsx';

/**
 * The nested step tree.
 *
 * Depth is capped at three, and the "Add child" affordance disappears at level
 * three rather than being shown and rejected — the constraint should be visible
 * in the interface, not discovered by trying.
 */
export function StepTree({
  workflow,
  connections,
  selectedId,
  runStatuses,
  editable,
  onSelect,
  onAddChild,
  onAddSibling,
  onDelete,
}: {
  workflow: WorkflowConfig;
  /** Needed only to name the bot half of a dual-identity connection. */
  connections?: Array<Pick<ConnectionDef, 'id' | 'name' | 'serviceAccountLabel' | 'credentialMode'>>;
  selectedId: string | null;
  runStatuses?: Record<string, string>;
  editable: boolean;
  onSelect(stepId: string): void;
  onAddChild(parentId: string): void;
  onAddSibling(afterStepId: string | null): void;
  onDelete(stepId: string): void;
}) {
  return (
    <div className="tree">
      {workflow.steps.map((step, index) => (
        <StepNode
          key={step.id}
          step={step}
          path={`${index + 1}`}
          workflow={workflow}
          connections={connections}
          selectedId={selectedId}
          runStatuses={runStatuses}
          editable={editable}
          onSelect={onSelect}
          onAddChild={onAddChild}
          onAddSibling={onAddSibling}
          onDelete={onDelete}
        />
      ))}

      {editable && (
        <button className="ghost" onClick={() => onAddSibling(null)}>
          + Add step
        </button>
      )}
    </div>
  );
}

function StepNode({
  step,
  path,
  workflow,
  connections,
  selectedId,
  runStatuses,
  editable,
  onSelect,
  onAddChild,
  onAddSibling,
  onDelete,
}: {
  step: StepConfig;
  path: string;
  workflow: WorkflowConfig;
  connections?: Array<Pick<ConnectionDef, 'id' | 'name' | 'serviceAccountLabel' | 'credentialMode'>>;
  selectedId: string | null;
  runStatuses?: Record<string, string>;
  editable: boolean;
  onSelect(stepId: string): void;
  onAddChild(parentId: string): void;
  onAddSibling(afterStepId: string | null): void;
  onDelete(stepId: string): void;
}) {
  const runtime = step.runtime ?? workflow.runtime;
  const status = runStatuses?.[step.id];
  const connectionCount = step.mcpConnections?.length ?? 0;
  // Who this step acts as, for every connection that has more than one option.
  // Shown for `auto` as well as the pinned values: with no chip at all, "the
  // identity depends on the trigger" looked exactly like "this connection has
  // only one identity", which is the confusion this is here to remove.
  const identities = (step.mcpConnections ?? [])
    .map((ref) => ({ ref, def: connections?.find((entry) => entry.id === ref.id) }))
    .filter(({ def }) => def && DUAL_IDENTITY.has(def.credentialMode));

  return (
    <div className="step-node">
      <div className="row" style={{ alignItems: 'stretch' }}>
        <button
          className="step-card"
          aria-current={selectedId === step.id}
          onClick={() => onSelect(step.id)}
        >
          <span className="step-index">{path}</span>
          <span className="step-body">
            <span className="step-name">{step.name}</span>
            <span className="step-meta">
              {status && <span className={`pill ${statusTone(status)}`}>{status}</span>}
              <span className={`pill ${runtime === 'container' ? 'warn' : ''}`}>{runtime}</span>
              {step.requiresApproval && <span className="pill accent">approval</span>}
              {typeof step.policy === 'string' && <span className="pill">{step.policy}</span>}
              {connectionCount > 0 && (
                <span className="pill">
                  +{connectionCount} connection{connectionCount === 1 ? '' : 's'}
                </span>
              )}
              {identities.map(({ ref, def }) => {
                const kind = identityKindFor(ref.actor ?? 'auto');
                const bot = botLabelFor(def!);
                return (
                  <IdentityBadge
                    key={`actor-${ref.id}`}
                    kind={kind}
                    label={`${ref.as} as ${kind === 'person' ? 'you' : kind === 'bot' ? bot : 'you or bot'}`}
                    tip={identityTip(kind, bot)}
                  />
                );
              })}
              {(step.skills?.length ?? 0) > 0 && (
                <span className="pill">{step.skills!.map((skill) => skill.name).join(', ')}</span>
              )}
            </span>
          </span>
        </button>

        {editable && (
          <div className="row" style={{ gap: '0.125rem' }}>
            {step.level < MAX_STEP_LEVEL && (
              <button
                className="ghost"
                title={`Add a level ${step.level + 1} child`}
                onClick={() => onAddChild(step.id)}
              >
                ⤷
              </button>
            )}
            <button className="ghost" title="Add a sibling below" onClick={() => onAddSibling(step.id)}>
              +
            </button>
            <button className="ghost danger" title="Delete step" onClick={() => onDelete(step.id)}>
              ×
            </button>
          </div>
        )}
      </div>

      {(step.childSteps?.length ?? 0) > 0 && (
        <div className="step-children">
          {step.childSteps!.map((child, index) => (
            <StepNode
              key={child.id}
              step={child}
              path={`${path}.${index + 1}`}
              workflow={workflow}
              connections={connections}
              selectedId={selectedId}
              runStatuses={runStatuses}
              editable={editable}
              onSelect={onSelect}
              onAddChild={onAddChild}
              onAddSibling={onAddSibling}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function statusTone(status: string): string {
  if (status === 'completed') return 'ok';
  if (status === 'failed') return 'danger';
  if (status === 'running') return 'accent';
  return 'warn';
}
